import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ItemActions } from '../ItemActions/ItemActions';

export type NoteToolboxProps = {
    visible: boolean;
    onEdit: () => void;
    onDelete: () => void;
    parentRef: React.RefObject<HTMLDivElement>;
};

type ToolboxPosition = {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    flexDirection: 'row' | 'column';
    transform?: string;
};

/**
 * Toolbox de gestion des notes avec positionnement intelligent
 * Se positionne automatiquement selon l'espace disponible autour de la note
 */
export const NoteToolbox: React.FunctionComponent<NoteToolboxProps> = function ({
    visible,
    onEdit,
    onDelete,
    parentRef,
}) {
    const [position, setPosition] = useState<ToolboxPosition>({
        top: 4,
        right: -40,
        flexDirection: 'column',
    });

    useEffect(() => {
        if (!visible || !parentRef.current) return;

        const noteElement = parentRef.current;
        const noteRect = noteElement.getBoundingClientRect();

        const containerElement =
            noteElement.closest('[data-scroll-container]') ||
            noteElement.closest('div[style*="overflow"]') ||
            document.documentElement;
        const containerRect = containerElement.getBoundingClientRect();

        const toolboxWidth = 36;
        const toolboxHorizontalHeight = 36;
        const spaceRight = containerRect.right - noteRect.right;
        const spaceAbove = noteRect.top - containerRect.top;
        const noteHeight = noteRect.height;
        const noteWidth = noteRect.width;
        const containerWidth = containerRect.width;

        let newPosition: ToolboxPosition;

        /**
         * Si la note prend plus de 70% de la largeur du conteneur,
         * on considère qu'elle prend "toute la largeur"
         */
        const noteTakesFullWidth = noteWidth > containerWidth * 0.7;

        if (noteTakesFullWidth) {
            if (spaceAbove >= toolboxHorizontalHeight + 8) {
                newPosition = {
                    bottom: noteRect.height + 8,
                    right: 4,
                    flexDirection: 'row',
                };
            } else {
                newPosition = {
                    left: noteRect.width + 4,
                    top: 4,
                    flexDirection: 'row',
                };
            }
        } else if (spaceRight >= toolboxWidth + 8) {
            const useHorizontalLayout = noteHeight < 50;
            newPosition = {
                top: useHorizontalLayout ? 4 : undefined,
                left: noteRect.width + 8,
                flexDirection: useHorizontalLayout ? 'row' : 'column',
                ...(useHorizontalLayout
                    ? {}
                    : {
                          top: noteHeight / 2,
                          transform: 'translateY(-50%)',
                      }),
            };
        } else if (spaceAbove >= toolboxHorizontalHeight + 8) {
            newPosition = {
                bottom: noteRect.height + 8,
                right: 4,
                flexDirection: 'row',
            };
        } else {
            const useHorizontalLayout = noteHeight < 50;
            newPosition = {
                top: 4,
                left: noteRect.width + 4,
                flexDirection: useHorizontalLayout ? 'row' : 'column',
            };
        }

        setPosition(newPosition);
    }, [visible, parentRef]);

    return (
        <Box
            sx={{
                position: 'absolute',
                ...position,
                display: 'flex',
                gap: position.flexDirection === 'row' ? '4px' : '2px',
                zIndex: 1000,
                flexDirection: position.flexDirection,
                transform: position.transform,
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <ItemActions
                onEdit={onEdit}
                onDelete={onDelete}
                editTooltip="Modifier cette note"
                deleteTooltip="Supprimer cette note"
                visible={visible}
                slideDirection="right"
                orientation={position.flexDirection}
            />
        </Box>
    );
};
